import React, { useEffect, useState, useMemo, useContext } from "react";
import { Tabs, Tooltip } from "antd";
import { useSelector, useDispatch } from "react-redux";
import { useParams, useSearchParams } from "react-router-dom";
import { useNavigate } from "react-router";
import {
  FileSpreadsheet,
  ChevronDown,
  Filter as FilterIcon,
  UserPlus,
  Tag as TagIcon,
} from "lucide-react";
import AboutCompany from "./AboutCompany";
import KeyExecutives from "./KeyExecutives";
import OrgChart from "./OrgChart";
import { getUserInfo } from "../../utils/utils";
import { errEnum, subscriptionPlans } from "../../config";
import TrialModal from "../../common/TrialModal";
import ViewEmailPhoneModal from "../../common/ViewEmailPhoneModal";
import {
  getEmployeeList,
  resetEmployeeList,
  getRelavantCompany,
  resetPostRelavantCompany,
  storeSelectedDepartment,
  emptyErrorObj,
  getCompanyTag,
  resetCompanyTag,
  downloadExecutiveExl,
  storeSelectedExecutive,
} from "../../actionCreator/companyDetailsActionCreator";
import { saveExcel } from "../../utils";
import { useExport } from "../executive-search/hooks/useExport";
import TagExecutive from "../executive-search/TagExecutive";
import { useAlert } from "../../context/AlertProvider";
import { AuthContext } from "../../context/AuthContext";
import FeatureComingSoon from "../../common/FeatureComingSoon";
import BulkAddToLead from "./BulkAddToLead";
import useUpgradeAlert from "../../common/hooks/useUpgradeAlert";

const SummaryContent = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const page = searchParams.get("page");
  const { showAlert } = useAlert();
  const showUpgradeAlert = useUpgradeAlert(() => setTabActiveKey("1"));
  const { id } = useParams();
  const { isAuthenticated: isLoggedIn, subscription } = useContext(AuthContext);
  const { getSchema, columns } = useExport();
  const [showModal, setShowModal] = useState(false);
  const [openModal, setOpenModal] = useState({
    info: null,
    open: false,
  });
  const [showEmailPhoneModal, setShowEmailPhoneModal] = useState(false);
  const [dropDownToggle, setDropdownToggle] = useState(false);
  const [tabActiveKey, setTabActiveKey] = useState("1");
  const [similarCount, setSimilarCount] = useState(5);
  const [selectedValue, setSelectedValue] = useState("Filter by Department");
  const [departmentAllList, setDepartmentAllList] = useState();
  const [openTagModal, setOpenTagModal] = useState(false);
  const [isAddToLead, setAddToLead] = useState(false);

  const [isApiFailed, setIsApiFailed] = useState(false);
  const departmentList = useSelector(
    (state) => state.companyDetailsReducer.departmentList,
  );
  const companyDetails = useSelector(
    (state) => state.companyDetailsReducer.companyDetails,
  );
  const similarCompanyList = useSelector(
    (state) => state.companyDetailsReducer.similarCompanyList,
  );

  const userAccountInfo = useSelector(
    (state) => state.CommonReducer.accountInfo,
  );

  const selectedEmployeeList = useSelector(
    (state) => state.companyDetailsReducer.selectedExecutive,
  );

  const selectedDepartment = useSelector(
    (state) => state.companyDetailsReducer.selectedDepartment,
  );

  const errObj = useSelector((state) => state.companyDetailsReducer.errObj);

  useEffect(() => {
    setIsApiFailed(!!Object.keys(errObj).length);
  }, [Object.keys(errObj).length]);

  useMemo(() => {
    if (Object.keys(getUserInfo()).length && companyDetails?.id) {
      const { id } = getUserInfo();
      dispatch(resetCompanyTag());
      dispatch(getCompanyTag(companyDetails?.id, id));
    }
  }, [companyDetails, userAccountInfo]);

  useMemo(() => {
    dispatch(resetPostRelavantCompany);
    if (Object.keys(getUserInfo()).length && companyDetails?.id) {
      const { id } = getUserInfo();
      dispatch(getRelavantCompany(id, companyDetails?.id));
    }
  }, [userAccountInfo]);

  useEffect(() => {
    setSimilarCount(
      similarCompanyList.length < 5 ? similarCompanyList.length : 5,
    );
  }, [similarCompanyList]);

  useEffect(() => {
    const dpt = [{ id: 0, name: "All" }, ...departmentList];
    setDepartmentAllList(dpt);
  }, [departmentList]);

  const checkLoginStatus = () => {
    if (isLoggedIn) {
      setShowModal(false);
    } else {
      setShowModal(true);
    }
  };

  const items = [
    {
      key: "1",
      label: "About Company",
      children: <AboutCompany checkLoginStatus={checkLoginStatus} />,
    },
    {
      key: "2",
      label: "Key Executives",
      children: <KeyExecutives />,
    },
    {
      key: "3",
      label: "Org. Chart",
      children: <OrgChart />,
    },
    {
      key: "4",
      label: isLoggedIn ? (
        <FeatureComingSoon>Triggers</FeatureComingSoon>
      ) : (
        "Triggers"
      ),
    },
  ];

  const onChange = (key) => {
    if (!isLoggedIn && (key === "2" || key === "3" || key === "4")) {
      setShowEmailPhoneModal(true);
    } else {
      setTabActiveKey(key);
      if (
        key === "3" &&
        [subscriptionPlans?.trail].includes(
          subscription?.subscriptionPackage?.name,
        )
      ) {
        showUpgradeAlert();
      } else {
        if (key !== "2" && key !== "3") {
          setSelectedValue("Filter by Department");
          dispatch(getEmployeeList(id, null, page));
        }
      }
    }
  };

  const toggleDropdown = () => {
    setDropdownToggle(!dropDownToggle);
  };

  const setValue = (selectedItem) => {
    dispatch(resetEmployeeList());
    dispatch(getEmployeeList(id, selectedItem?.id, page));
    dispatch(storeSelectedDepartment(selectedItem?.id));
    setSelectedValue(selectedItem?.name);
    setDropdownToggle(!dropDownToggle);
    if (tabActiveKey === "3") {
      setTabActiveKey("3");
    } else {
      setTabActiveKey("2");
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setOpenModal(false);
    dispatch(emptyErrorObj());
  };

  const redirectToSignup = () => {
    setShowModal(false);
    navigate("/signup");
  };

  const tagExecutive = () => {
    checkLoginStatus();
    if (isLoggedIn) {
      if (selectedEmployeeList?.length) {
        setOpenTagModal(true);
      } else {
        showAlert("warning", "please select record.");
      }
    }
  };

  const downloadExcel = async () => {
    checkLoginStatus();
    if (isLoggedIn) {
      let selectedEmpIds = "";
      selectedEmployeeList?.forEach((exe) => {
        selectedEmpIds += `${exe?.id},`;
      });
      selectedEmpIds = selectedEmpIds.substring(
        selectedEmpIds.lastIndexOf(","),
        0,
      );
      const payload = {
        empIds: selectedEmpIds,
        department: selectedDepartment,
        companyId: companyDetails?.id,
      };
      const res = await dispatch(downloadExecutiveExl(payload));
      if (res?.length) {
        const downloadedUpdatedData = getSchema(res);
        const fileName = companyDetails.name + "-executive-data";
        saveExcel(downloadedUpdatedData, columns, fileName);
        showAlert(
          "success",
          "Download Complete",
          <div>
            The key executive data for the {companyDetails.name} has been
            successfully downloaded to{" "}
            <FileSpreadsheet className="tw-inline tw-text-emerald-600" size={16} />.
          </div>,
        );
      }
    }
  };

  const closeTagModal = () => {
    setOpenTagModal(false);
  };

  const handleAddToLead = () => {
    if (selectedEmployeeList?.length) {
      setAddToLead(true);
    } else {
      showAlert("warning", "please select record.");
    }
  };

  return (
    <>
      <div className="tw-flex tw-items-center tw-justify-between tw-gap-2 tw-mt-4">
        {isLoggedIn && (
          <div className="tw-relative">
            <div
              className="tw-text-sm tw-font-inter tw-cursor-pointer tw-flex tw-items-center tw-justify-between tw-gap-2.5 tw-py-2 tw-px-4 tw-rounded-xl tw-border tw-border-solid tw-border-slate-200 tw-bg-white hover:tw-border-theme hover:tw-bg-indigo-50/10 tw-text-slate-600 hover:tw-text-theme tw-transition-all tw-w-[220px]"
              onClick={toggleDropdown}
            >
              <div className="tw-flex tw-items-center tw-gap-2 tw-min-w-0">
                <FilterIcon size={16} className="tw-opacity-80 tw-shrink-0" />
                <span className="tw-font-medium tw-truncate tw-whitespace-nowrap">{selectedValue}</span>
              </div>
              <ChevronDown size={14} className="tw-opacity-65 tw-shrink-0" />
            </div>
            {dropDownToggle && (
              <ul className="tw-rounded-xl tw-shadow-lg tw-border tw-border-solid tw-border-slate-200 tw-bg-white tw-py-1.5 tw-absolute tw-z-50 tw-mt-1.5 tw-w-[220px] tw-max-h-[220px] tw-overflow-y-auto tw-m-0 tw-list-none tw-p-0">
                {departmentAllList?.map((item) => (
                  <li
                    key={item?.id}
                    onClick={() => setValue(item)}
                    className="tw-px-4 tw-py-2 hover:tw-bg-indigo-50/40 tw-text-sm tw-text-slate-650 hover:tw-text-theme tw-cursor-pointer tw-transition-colors"
                  >
                    {item?.name}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
        <div className="tw-flex tw-items-center tw-gap-2.5 tw-ml-auto">
          {!!selectedEmployeeList?.length && (
            <Tooltip title="Add to lead">
              <div
                className="tw-cursor-pointer tw-flex tw-items-center tw-justify-center tw-size-10 tw-rounded-xl tw-border tw-border-solid tw-border-slate-200 hover:tw-border-theme/35 hover:tw-bg-indigo-50/20 tw-text-slate-500 hover:tw-text-theme tw-transition-all"
                onClick={() => {
                  if (isLoggedIn) {
                    handleAddToLead();
                  } else {
                    checkLoginStatus();
                  }
                }}
              >
                <UserPlus size={18} />
              </div>
            </Tooltip>
          )}
          <Tooltip title="Tag Executive">
            <div
              className="tw-cursor-pointer tw-flex tw-items-center tw-justify-center tw-size-10 tw-rounded-xl tw-border tw-border-solid tw-border-slate-200 hover:tw-border-theme/35 hover:tw-bg-indigo-50/20 tw-text-slate-500 hover:tw-text-theme tw-transition-all"
              onClick={() => {
                tagExecutive();
              }}
            >
              <TagIcon size={18} />
            </div>
          </Tooltip>
          <Tooltip title="Download as Excel">
            <div
              className="tw-cursor-pointer tw-flex tw-items-center tw-justify-center tw-size-10 tw-rounded-xl tw-border tw-border-solid tw-border-slate-200 hover:tw-border-theme/35 hover:tw-bg-indigo-50/20 tw-text-emerald-600 hover:tw-text-emerald-700 tw-transition-all"
              onClick={() => downloadExcel()}
            >
              <FileSpreadsheet size={18} />
            </div>
          </Tooltip>
        </div>
      </div>
      <Tabs
        activeKey={tabActiveKey}
        items={items}
        onChange={onChange}
        className="tw-border tw-border-solid !tw-border-slate-200/80 !tw-rounded-2xl tw-bg-white tw-shadow-sm tw-overflow-hidden tw-mt-4"
      />

      <TrialModal
        openModal={showModal}
        closeModal={closeModal}
        redirectToSignup={redirectToSignup}
        redirect={true}
      />

      <ViewEmailPhoneModal
        open={showEmailPhoneModal}
        onClose={() => setShowEmailPhoneModal(false)}
      />

      {openModal?.open && (
        <TrialModal
          openModal={openModal}
          closeModal={closeModal}
          redirectToSignup={redirectToSignup}
          redirect={true}
          isSubscriptionMissing
        />
      )}

      {isApiFailed ? (
        <TrialModal
          openModal={isApiFailed}
          closeModal={closeModal}
          redirectToSignup={redirectToSignup}
          buttonText="OK"
          modalBody={
            <div id="small-dialog2">
              {errObj[errEnum.DOWNLOAD_ERROR]
                ? errObj[errEnum.DOWNLOAD_ERROR].message
                : "Please call System support. You application having some issue."}
            </div>
          }
          modalWidth="400px"
        />
      ) : (
        ""
      )}

      <TagExecutive
        selectedRecords={selectedEmployeeList?.map((d) => d?.id)}
        showTagModal={openTagModal && !!selectedEmployeeList?.length}
        closeTagModal={closeTagModal}
      />
      <BulkAddToLead
        selectedItems={selectedEmployeeList?.map((d) => d?.leads)}
        isOpen={isAddToLead}
        handleCancel={() => setAddToLead(false)}
        callback={() => {
          dispatch(getEmployeeList(companyDetails?.id, null, page));
          dispatch(storeSelectedExecutive([]));
        }}
      />
    </>
  );
};

export default SummaryContent;
